import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { extractACMEErrorMessage } from '@b2b/types/core';

import { ContentHeaderService } from '../../core/content-header/content-header.service';
import { PendoService } from '../../core/pendo/pendo.service';
import { PhotosApiService } from '../../core/api/photos/photos-api.service';
import { GiftCardsApiService } from '../../core/api/gift-cards/gift-cards-api.service';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const ALLOW_CUSTOM_AMOUNT_CLICKED_EVENT = 'Allow Custom Amount - Clicked';
const ALLOW_CUSTOM_AMOUNT_UNCLICKED_EVENT = 'Allow Custom Amount - Unclicked';
const MIN_MAX_AMOUNT_CLICKED_EVENT = 'Min/Max Amount - Clicked';
const MIN_MAX_AMOUNT_UNCLICKED_EVENT = 'Min/Max Amount - Unclicked';
const PRESET_AMOUNTS_EVENT = 'Preset Amounts';

/**
 * Amount presets accept up to 2 decimal places, matching how prices are
 * formatted elsewhere in the app (e.g. $19.99). MIN/MAX_AMOUNT are the
 * fallback bounds used only while "Set Minimum and Maximum Gift Card
 * amounts" is off — once checked, the venue's own customAmountMin/Max
 * (DEV-13995) supersede these as the preset bound.
 */
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 99999;

/** AC (DEV-13995): ACME's industry-wide bounds for a venue's custom-amount min/max. */
const CUSTOM_AMOUNT_INDUSTRY_MIN = 1;
const CUSTOM_AMOUNT_INDUSTRY_MAX = 2000;

/** AC1: 4 preset rows pre-populated at $20/$50/$100/$200 when no product exists yet. */
const DEFAULT_PRESET_AMOUNTS: ReadonlyArray<{ name: string; description: string; amount: number }> = [
  { name: '$20', description: '', amount: 20 },
  { name: '$50', description: '', amount: 50 },
  { name: '$100', description: '', amount: 100 },
  { name: '$200', description: '', amount: 200 },
];

export interface AmountRowErrors {
  name?: string;
  amount?: string;
}

export interface CustomAmountRangeErrors {
  min?: string;
  max?: string;
}

/** A venue's fully-configured custom-amount range, parsed to numbers - both bounds always present (see `currentCustomAmountRange`). */
export interface CustomAmountRange {
  min: number;
  max: number;
}

export interface AmountRow {
  id: number | string | null;
  name: string;
  description: string;
  amount: string;
  errors: AmountRowErrors;
}

/** Draft state for the "Edit Amount" modal (screenshot: DEV-13071) — a copy of the targeted row, not written back until Update succeeds. */
export interface AmountEditorState {
  index: number;
  name: string;
  description: string;
  amount: string;
  errors: AmountRowErrors;
}

export interface ImageRow {
  id: number | string | null;
  imageId: number | string;
  description: string;
  primary: boolean;
  /**
   * Displayable image URL. For a file just picked in this session (before the
   * first save), this is a local `URL.createObjectURL` blob URL. Once the row
   * is persisted, it's the server-computed S3 `preview` URL (see
   * `GiftCardProductImage.preview`) — `save()` swaps the blob URL for it and
   * revokes the blob URL. Rows loaded from the server on `load()` already
   * carry the server URL directly.
   */
  previewUrl?: string;
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Purely presentational — doesn't touch the underlying stored value. Matches the `currency` pipe formatting used elsewhere in the app (thousands separators, 2 decimal places). */
export function formatAmountDisplay(amount: string): string {
  const numeric = Number(amount);
  return Number.isFinite(numeric) && amount.trim() !== '' ? CURRENCY_FORMATTER.format(numeric) : '';
}

/**
 * Pure so it's directly unit-testable without standing up the component.
 * `customRange` — when "Set Minimum and Maximum Gift Card amounts" is
 * checked with a configured min/max (DEV-13995), a preset amount must also
 * fall within that range: a preset outside it would be un-purchasable by
 * any channel enforcing the same bounds. Falls back to the placeholder
 * MIN/MAX_AMOUNT when omitted (range not enabled, or not configured yet).
 */
export function validateAmountRow(row: { name: string; amount: string }, customRange?: CustomAmountRange): AmountRowErrors {
  const errors: AmountRowErrors = {};
  const name = row.name.trim();
  const amount = row.amount.trim();

  if (!name) {
    errors.name = 'Name is required.';
  }

  if (!amount) {
    errors.amount = 'Amount is required.';
  } else if (!AMOUNT_PATTERN.test(amount)) {
    errors.amount = 'Amount must be a number with up to 2 decimal places.';
  } else {
    const numeric = Number(amount);
    const min = customRange?.min ?? MIN_AMOUNT;
    const max = customRange?.max ?? MAX_AMOUNT;
    if (numeric < min || numeric > max) {
      errors.amount = customRange
        ? `Amount should be between ${formatAmountDisplay(String(min))} and ${formatAmountDisplay(String(max))}.`
        : `Amount must be between ${min} and ${max}.`;
    }
  }

  return errors;
}

/**
 * AC (DEV-13995): while "Set Minimum and Maximum Gift Card amounts" is
 * checked, min/max are required and must fall within ACME's industry-wide
 * bounds ($1.00–$2,000.00), with min strictly less than max. Pure so it's
 * directly unit-testable.
 */
export function validateCustomAmountRange(range: { min: string; max: string }): CustomAmountRangeErrors {
  const errors: CustomAmountRangeErrors = {};
  const minRaw = range.min.trim();
  const maxRaw = range.max.trim();

  if (!minRaw) {
    errors.min = 'Minimum amount is required.';
  } else if (!AMOUNT_PATTERN.test(minRaw)) {
    errors.min = 'Minimum amount must be a number with up to 2 decimal places.';
  } else if (Number(minRaw) < CUSTOM_AMOUNT_INDUSTRY_MIN) {
    errors.min = `Minimum amount must be at least ${formatAmountDisplay(String(CUSTOM_AMOUNT_INDUSTRY_MIN))}.`;
  }

  if (!maxRaw) {
    errors.max = 'Maximum amount is required.';
  } else if (!AMOUNT_PATTERN.test(maxRaw)) {
    errors.max = 'Maximum amount must be a number with up to 2 decimal places.';
  } else if (Number(maxRaw) > CUSTOM_AMOUNT_INDUSTRY_MAX) {
    errors.max = `Maximum amount cannot exceed ${formatAmountDisplay(String(CUSTOM_AMOUNT_INDUSTRY_MAX))}.`;
  }

  if (!errors.min && !errors.max && Number(minRaw) >= Number(maxRaw)) {
    errors.max = 'Maximum amount must be greater than the minimum amount.';
  }

  return errors;
}

/**
 * Gift Cards product config settings page (DEV-13071).
 *
 * Single-record form: on load, GET the product list (empty → create, non-empty → update).
 * Preset amounts: fixed 4 rows, edit via modal that PUTs individual rows to `/amounts/{id}` endpoint.
 * Images: add/remove/primary via nested `/images` endpoints. The image
 * `description` (caption) isn't editable since nothing displays it
 * (DEV-14444); it's still round-tripped so existing values aren't wiped.
 */
@Component({
  selector: 'app-gift-cards',
  standalone: true,
  template: `
    <div class="gift-cards-page">
      @if (loading()) {
        <p>Loading&hellip;</p>
      } @else {
        @if (errorMessage()) {
          <div class="alert alert-danger" role="alert">{{ errorMessage() }}</div>
        }
        @if (successMessage()) {
          <div class="alert alert-success" role="status">{{ successMessage() }}</div>
        }

        <h4 class="gift-cards-section-title">Preset Amounts</h4>

        <table class="gift-cards-amounts-table">
          <caption class="sr-only">Preset Amounts</caption>
          <thead>
            <tr>
              <th scope="col">Edit</th>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col" class="gift-cards-amount-col">Amount</th>
            </tr>
          </thead>
          <tbody>
            @for (row of amounts(); track $index; let i = $index) {
              <tr>
                <td>
                  <a href="javascript:void(0)" class="gift-cards-edit-link" (click)="openAmountEditor(i)">Edit</a>
                </td>
                <td><span>{{ row.name }}</span></td>
                <td><span>{{ row.description }}</span></td>
                <td class="gift-cards-amount-col"><span>{{ formatAmountDisplay(row.amount) }}</span></td>
              </tr>
            }
          </tbody>
        </table>

        <div class="checkbox gift-cards-allow-custom">
          <label>
            <input type="checkbox" [checked]="allowCustomAmount()" (change)="toggleAllowCustomAmount()" />
            Allow Custom Amount
          </label>
        </div>

        @if (allowCustomAmount()) {
          <div class="checkbox gift-cards-set-custom-amount-range">
            <label>
              <input
                type="checkbox"
                [checked]="customAmountRangeEnabled()"
                (change)="toggleCustomAmountRangeEnabled()"
              />
              Set Minimum and Maximum Gift Card amounts
            </label>
          </div>

          @if (customAmountRangeEnabled()) {
            <div class="form-inline gift-cards-custom-amount-range">
              <div class="form-group gift-cards-custom-amount-field" [class.has-error]="customAmountRangeErrors().min">
                <label for="gift-cards-custom-amount-min">Minimum</label>
                <input
                  type="text"
                  id="gift-cards-custom-amount-min"
                  class="form-control"
                  inputmode="decimal"
                  placeholder="0.00"
                  [value]="customAmountMin()"
                  (input)="updateCustomAmountField('min', $event)"
                />
              </div>
              <div class="form-group gift-cards-custom-amount-field" [class.has-error]="customAmountRangeErrors().max">
                <label for="gift-cards-custom-amount-max">Maximum</label>
                <input
                  type="text"
                  id="gift-cards-custom-amount-max"
                  class="form-control"
                  inputmode="decimal"
                  placeholder="0.00"
                  [value]="customAmountMax()"
                  (input)="updateCustomAmountField('max', $event)"
                />
              </div>
            </div>
            @if (customAmountRangeErrors().min || customAmountRangeErrors().max) {
              <div class="gift-cards-custom-amount-range-errors">
                @if (customAmountRangeErrors().min) {
                  <span class="help-block">{{ customAmountRangeErrors().min }}</span>
                }
                @if (customAmountRangeErrors().max) {
                  <span class="help-block">{{ customAmountRangeErrors().max }}</span>
                }
              </div>
            }
          }
        }

        <hr class="gift-cards-divider" />

        <div class="gift-card-designs">
          <h4 class="gift-cards-section-title">Gift Cards Designs</h4>
          <p class="gift-cards-hint">The image guests choose when buying a gift card. The primary is pre-selected by default.</p>
          <button type="button" class="btn gift-cards-add-files-btn" (click)="fileInput.click()">
            <span class="glyphicon glyphicon-plus" aria-hidden="true"></span> Add Files&hellip;
          </button>
          <input
            #fileInput
            type="file"
            accept="image/*"
            multiple
            hidden
            class="gift-cards-file-input"
            (change)="onFilesSelected($event)"
          />

          @if (images().length === 0 && uploadingFiles().length === 0) {
            <p class="gift-cards-no-items">No items found</p>
          } @else {
            <div class="gift-card-image-list">
              @for (image of images(); track $index; let i = $index) {
                <div class="gift-card-image-card">
                  <div class="gift-card-image-box">
                    <div class="gift-card-image-controls">
                      @if (i > 0) {
                        <button
                          type="button"
                          class="gift-card-image-move-btn"
                          [attr.aria-label]="'Move image ' + (i + 1) + ' left'"
                          (click)="moveImage(i, -1)"
                        >
                          <span class="glyphicon glyphicon-chevron-left" aria-hidden="true"></span>
                        </button>
                      } @else {
                        <span class="gift-card-image-move-btn-spacer"></span>
                      }
                      <span class="gift-card-image-grip" aria-hidden="true">
                        <span class="gift-card-image-grip-bar"></span>
                        <span class="gift-card-image-grip-bar"></span>
                        <span class="gift-card-image-grip-bar"></span>
                      </span>
                      @if (i < images().length - 1) {
                        <button
                          type="button"
                          class="gift-card-image-move-btn"
                          [attr.aria-label]="'Move image ' + (i + 1) + ' right'"
                          (click)="moveImage(i, 1)"
                        >
                          <span class="glyphicon glyphicon-chevron-right" aria-hidden="true"></span>
                        </button>
                      } @else {
                        <span class="gift-card-image-move-btn-spacer"></span>
                      }
                    </div>
                    <div class="gift-card-image-frame">
                      @if (image.previewUrl) {
                        <img [src]="image.previewUrl" alt="" class="gift-card-image-preview" />
                      } @else {
                        <div class="gift-card-image-placeholder">
                          <span class="glyphicon glyphicon-picture" aria-hidden="true"></span>
                        </div>
                      }
                      @if (image.primary) {
                        <div class="gift-card-image-primary-badge">Primary</div>
                      } @else {
                        <button
                          type="button"
                          class="gift-card-image-make-primary-btn"
                          (click)="setPrimaryImage(i)"
                        >Make Primary</button>
                      }
                      <button
                        type="button"
                        class="gift-cards-remove-btn"
                        [attr.aria-label]="'Remove image ' + (i + 1)"
                        (click)="removeImage(i)"
                      >Delete</button>
                    </div>
                  </div>
                </div>
              }
              @for (uploading of uploadingFiles(); track uploading.id) {
                <div class="gift-card-image-card gift-card-image-uploading">
                  <div class="gift-card-image-box">
                    <div class="gift-card-image-frame">
                      <div class="gift-card-image-progress">
                        <div class="progress progress-striped active">
                          <div
                            class="progress-bar progress-bar-success"
                            role="progressbar"
                            [attr.aria-valuenow]="uploading.progress"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            [style.width.%]="uploading.progress"
                          ></div>
                        </div>
                        <p class="gift-card-image-uploading-name">{{ uploading.name }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <hr class="gift-cards-divider" />
        <div class="gift-cards-bottom-actions">
          <button type="button" class="btn btn-primary" [disabled]="!canSave()" (click)="save()">
            Update Gift Cards
          </button>
        </div>
      }

      @if (editingAmount(); as editor) {
        <div class="modal-backdrop fade in"></div>
        <div
          class="modal fade in"
          style="display: block;"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gift-cards-amount-modal-title"
          tabindex="-1"
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h3 class="modal-title" id="gift-cards-amount-modal-title">Edit Amount</h3>
              </div>
              <div class="modal-body">
                <div class="form-group" [class.has-error]="editor.errors.name">
                  <label for="gift-cards-amount-name">Name</label>
                  <input
                    type="text"
                    id="gift-cards-amount-name"
                    class="form-control"
                    [value]="editor.name"
                    (input)="updateAmountEditorField('name', $event)"
                  />
                  @if (editor.errors.name) {
                    <span class="help-block">{{ editor.errors.name }}</span>
                  }
                </div>
                <div class="form-group">
                  <label for="gift-cards-amount-description">Description</label>
                  <input
                    type="text"
                    id="gift-cards-amount-description"
                    class="form-control"
                    [value]="editor.description"
                    (input)="updateAmountEditorField('description', $event)"
                  />
                </div>
                <div class="form-group" [class.has-error]="editor.errors.amount">
                  <label for="gift-cards-amount-amount">Amount</label>
                  <input
                    type="text"
                    id="gift-cards-amount-amount"
                    class="form-control"
                    inputmode="decimal"
                    placeholder="0.00"
                    [value]="editor.amount"
                    (input)="updateAmountEditorField('amount', $event)"
                  />
                  @if (editor.errors.amount) {
                    <span class="help-block">{{ editor.errors.amount }}</span>
                  }
                </div>
              </div>
              <div class="modal-footer gift-cards-amount-modal-footer">
                <button type="button" class="btn btn-link gift-cards-cancel-btn" (click)="closeAmountEditor()">Cancel</button>
                <button
                  type="button"
                  class="btn btn-primary"
                  [disabled]="amountEditorSaving()"
                  (click)="submitAmountEditor()"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class GiftCardsComponent implements OnInit {
  private readonly api = inject(GiftCardsApiService);
  private readonly photos = inject(PhotosApiService);
  private readonly contentHeader = inject(ContentHeaderService);
  private readonly pendo = inject(PendoService);

  /** Exposed as an instance member so the template can call it — Angular templates can't invoke a bare imported function. */
  readonly formatAmountDisplay = formatAmountDisplay;

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  readonly productId = signal<number | string | null>(null);
  /**
   * No UI toggle exists for this yet (out of scope per the reviewed
   * screenshot), but the backend column is NOT-NULL — must always be sent on
   * create/update. Defaults to `true` for a first-time save (saving settings
   * activates gift cards); preserved from the loaded product otherwise.
   */
  readonly active = signal(true);
  readonly allowCustomAmount = signal(true);
  /**
   * "Set Minimum and Maximum Gift Card amounts" (DEV-13995) - a separate, nested checkbox
   * shown only while `allowCustomAmount` is checked (per the design screenshot). This, not
   * `allowCustomAmount`, is the actual gate for requiring/enforcing/nulling customAmountMin/Max.
   * It is NOT a field the backend persists - on `load()` it's derived from whether a min/max
   * came back on the product; on `save()` it just decides whether the current
   * customAmountMin/Max signals get sent as-is or forced to null. Defaults to `false` for a
   * brand-new product - opt-in, not on by default.
   */
  readonly customAmountRangeEnabled = signal(false);
  /** String-backed like `AmountRow.amount`, to bind directly to the input and defer parsing until save/validation. */
  readonly customAmountMin = signal('');
  readonly customAmountMax = signal('');
  readonly customAmountRangeErrors = signal<CustomAmountRangeErrors>({});
  readonly amounts = signal<AmountRow[]>([]);
  readonly images = signal<ImageRow[]>([]);
  /** One entry per file currently mid-upload — same progress feedback the legacy event-template upload directive shows. Removed once the upload settles (success or failure). */
  readonly uploadingFiles = signal<{ id: number; name: string; progress: number }[]>([]);
  /**
   * Keys uploadingFiles entries — file.name alone collides if two
   * concurrently-queued uploads (possible since onFilesSelected() fires
   * addFiles() without awaiting) share a name, cross-applying progress
   * updates and removing both cards on the first one to finish.
   */
  private uploadSeq = 0;

  readonly editingAmount = signal<AmountEditorState | null>(null);
  readonly amountEditorSaving = signal(false);

  /**
   * Amount rows are only ever committed (locally or via the API) through
   * `submitAmountEditor()`, which already blocks on an invalid draft before
   * it's ever written back to `amounts()` — so there's no longer a reachable
   * UI path that leaves a row in `amounts()` with a validation error for this
   * button to gate on. `save()` still re-validates defensively before
   * bundling amounts into a create() call (see its own doc comment).
   */
  readonly canSave = computed(() => !this.loading() && !this.saving());

  /**
   * Drives the app shell's single, persistent header bar (the same
   * `ContentHeaderService` singleton the legacy `content-header` directive
   * reads from — shared, not duplicated, via ngUpgrade's downgrade). Do NOT
   * also render a header inside this component's own template: an earlier
   * version did, which produced two "Gift Cards" bars on screen at once.
   *
   * Called explicitly at every point `canSave()` can change (load complete,
   * save start/end, per-row validation), rather than via `effect()` — an
   * effect here never flushed in this app's TestBed setup without a
   * `detectChanges()`/tick call at every assertion site, which would make
   * every test in this suite fragile for a timing reason unrelated to what
   * they're actually testing. Explicit calls are synchronous and simple to
   * reason about instead.
   */
  private syncContentHeader(): void {
    this.contentHeader.update({
      title: 'Gift Cards',
      buttonRight: {
        label: 'Update Gift Cards',
        action: () => this.save(),
        isDisabled: !this.canSave(),
      },
    });
  }

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const response = await this.api.list();
      const product = response.data.payload?.list?.[0];

      if (product) {
        this.productId.set(product.id ?? null);
        this.active.set(product.active ?? true);
        this.allowCustomAmount.set(product.allowCustomAmount ?? true);
        /* "Set Minimum and Maximum Gift Card amounts" isn't a persisted flag (DEV-13995) - the
           checkbox is inferred purely from whether a min or max is stored, and only while
           Allow Custom Amount is also on. Consistent with toggleAllowCustomAmount(): whenever
           the range is inapplicable (Allow Custom Amount off), it's cleared here too rather
           than silently carried in signals that never surface in the UI - so a save made while
           Allow Custom Amount is off can't resurrect/persist a stale range untouched by the
           admin. Never auto-fills a missing bound to the industry default here (only
           toggleCustomAmountRangeEnabled() does that, for a fresh in-session check) - a product
           stored with only one bound configured is left exactly as stored, so an admin who opens
           the page and saves without touching anything can't silently persist a value they never
           entered. */
        const hasConfiguredRange = product.customAmountMin != null || product.customAmountMax != null;
        if (this.allowCustomAmount() && hasConfiguredRange) {
          this.customAmountRangeEnabled.set(true);
          this.customAmountMin.set(product.customAmountMin != null ? String(product.customAmountMin) : '');
          this.customAmountMax.set(product.customAmountMax != null ? String(product.customAmountMax) : '');
        } else {
          this.customAmountRangeEnabled.set(false);
          this.customAmountMin.set('');
          this.customAmountMax.set('');
        }
        this.amounts.set((product.amounts ?? []).map((row) => this.toAmountRow(row)));

        const imagesResponse = await this.api.images.list(product.id as number | string);
        this.images.set((imagesResponse.data.payload ?? []).map((row) => this.toImageRow(row)));
      } else {
        this.productId.set(null);
        this.active.set(true);
        this.allowCustomAmount.set(true);
        this.customAmountRangeEnabled.set(false);
        this.customAmountMin.set('');
        this.customAmountMax.set('');
        this.amounts.set(DEFAULT_PRESET_AMOUNTS.map((preset) => this.toAmountRow(preset)));
        this.images.set([]);
      }
      this.customAmountRangeErrors.set({});
    } catch (error) {
      this.errorMessage.set(this.extractErrorMessage(error));
    } finally {
      this.loading.set(false);
      this.syncContentHeader();
    }
  }

  /**
   * Unchecking this also clears any configured custom-amount range - same
   * rule as `load()` (a range is never carried while Allow Custom Amount is
   * off) and as unchecking "Set Minimum and Maximum" itself: re-checking
   * later starts from the $1.00/$2,000.00 defaults, not old numbers.
   */
  toggleAllowCustomAmount(): void {
    const next = !this.allowCustomAmount();
    this.allowCustomAmount.set(next);
    this.pendo.track(next ? ALLOW_CUSTOM_AMOUNT_CLICKED_EVENT : ALLOW_CUSTOM_AMOUNT_UNCLICKED_EVENT);
    if (!next) {
      this.customAmountRangeEnabled.set(false);
      this.customAmountMin.set('');
      this.customAmountMax.set('');
      this.customAmountRangeErrors.set({});
    }
  }

  toggleCustomAmountRangeEnabled(): void {
    const next = !this.customAmountRangeEnabled();
    this.customAmountRangeEnabled.set(next);
    this.pendo.track(next ? MIN_MAX_AMOUNT_CLICKED_EVENT : MIN_MAX_AMOUNT_UNCLICKED_EVENT);
    if (next) {
      this.applyCustomAmountDefaults();
    } else {
      /* AC (DEV-13995): unchecking clears min/max to null (per explicit instruction), not
         merely stops enforcing them - re-checking later starts from the $1/$2,000 defaults
         again, not the venue's old numbers. */
      this.customAmountMin.set('');
      this.customAmountMax.set('');
      this.customAmountRangeErrors.set({});
    }
  }

  /**
   * AC (DEV-13995): ACME defaults to the industry limits ($1.00/$2,000.00)
   * whenever "Set Minimum and Maximum Gift Card amounts" is checked with no
   * min/max configured yet - pre-filled, still editable. Stored as a plain
   * number string (`'1'`, not `'1.00'`), matching how preset amounts are
   * represented in this component (`toAmountRow`'s `String(vo.amount)`) -
   * same mask/formatting as the Amount field: a raw decimal string, only
   * currency-formatted for read-only display via `formatAmountDisplay`.
   */
  private applyCustomAmountDefaults(): void {
    if (!this.customAmountMin().trim()) {
      this.customAmountMin.set(String(CUSTOM_AMOUNT_INDUSTRY_MIN));
    }
    if (!this.customAmountMax().trim()) {
      this.customAmountMax.set(String(CUSTOM_AMOUNT_INDUSTRY_MAX));
    }
  }

  updateCustomAmountField(field: 'min' | 'max', event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (field === 'min') {
      this.customAmountMin.set(value);
    } else {
      this.customAmountMax.set(value);
    }
    this.customAmountRangeErrors.set(validateCustomAmountRange({ min: this.customAmountMin(), max: this.customAmountMax() }));
  }

  /**
   * `undefined` while "Set Minimum and Maximum Gift Card amounts" is off, or while it's on but
   * either bound is blank/unparseable - callers fall back to the placeholder preset bounds
   * rather than enforcing (and messaging) a half-configured range. A partially-typed range
   * (e.g. min filled, max still blank) is never treated as "configured" here; `save()` validates
   * the range's own shape (required/industry bounds/min<max) before this is ever used to bound
   * preset validation, so a genuinely invalid range always surfaces its own error first.
   */
  private currentCustomAmountRange(): CustomAmountRange | undefined {
    if (!this.customAmountRangeEnabled()) {
      return undefined;
    }
    const min = this.parseAmountSignal(this.customAmountMin());
    const max = this.parseAmountSignal(this.customAmountMax());
    if (min == null || max == null) {
      return undefined;
    }
    return { min, max };
  }

  /**
   * A configured range must cover every existing preset - a preset outside it would be
   * un-purchasable by any channel enforcing the same bounds. Reported as a specific, actionable
   * error on the Min/Max fields themselves (naming the actual conflicting preset amount) rather
   * than flagging individual rows in the Preset Amounts table, which has no per-row error
   * display for the admin to ever see.
   */
  private validateRangeAgainstPresets(range: CustomAmountRange): CustomAmountRangeErrors {
    const errors: CustomAmountRangeErrors = {};
    const presetAmounts = this.amounts()
      .map((row) => Number(row.amount))
      .filter((value) => Number.isFinite(value));
    if (presetAmounts.length === 0) {
      return errors;
    }
    const lowestPreset = Math.min(...presetAmounts);
    const highestPreset = Math.max(...presetAmounts);
    if (lowestPreset < range.min) {
      errors.min = `Minimum amount cannot be higher than the lowest preset amount (${formatAmountDisplay(String(lowestPreset))}).`;
    }
    if (highestPreset > range.max) {
      errors.max = `Maximum amount cannot be lower than the highest preset amount (${formatAmountDisplay(String(highestPreset))}).`;
    }
    return errors;
  }

  private parseAmountSignal(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  openAmountEditor(index: number): void {
    this.pendo.track(PRESET_AMOUNTS_EVENT);
    const row = this.amounts()[index];
    this.editingAmount.set({ index, name: row.name, description: row.description, amount: row.amount, errors: {} });
  }

  closeAmountEditor(): void {
    this.editingAmount.set(null);
  }

  updateAmountEditorField(field: 'name' | 'description' | 'amount', event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const current = this.editingAmount();
    if (!current) {
      return;
    }
    const updated: AmountEditorState = { ...current, [field]: value };
    updated.errors = validateAmountRow(updated, this.currentCustomAmountRange());
    this.editingAmount.set(updated);
  }

  /**
   * The product-level "Update Gift Cards" button no longer touches amounts
   * at all once a product exists (see `save()`) - this is the only way to
   * persist a change to an existing preset amount. Before the product's
   * first save there's no id to PUT against yet, so the edit is staged
   * locally instead and gets bundled into the create() call in `save()`.
   */
  async submitAmountEditor(): Promise<void> {
    const draft = this.editingAmount();
    if (!draft) {
      return;
    }

    const errors = validateAmountRow(draft, this.currentCustomAmountRange());
    if (errors.name || errors.amount) {
      this.editingAmount.set({ ...draft, errors });
      return;
    }

    const row = this.amounts()[draft.index];
    const productId = this.productId();
    const name = draft.name.trim();
    const description = draft.description.trim();
    const amount = Number(draft.amount);

    if (row.id != null && productId != null) {
      this.amountEditorSaving.set(true);
      try {
        const response = await this.api.amounts.update(productId, { id: row.id, name, description, amount });
        const saved = this.toAmountRow(response.data.payload);
        this.amounts.set(this.amounts().map((r, i) => (i === draft.index ? saved : r)));
        this.editingAmount.set(null);
      } catch (error) {
        this.editingAmount.set({ ...draft, errors: { amount: this.extractErrorMessage(error) } });
      } finally {
        this.amountEditorSaving.set(false);
      }
      return;
    }

    this.amounts.set(
      this.amounts().map((r, i) => (i === draft.index ? { ...r, name, description, amount: draft.amount, errors: {} } : r)),
    );
    this.editingAmount.set(null);
  }

  /**
   * Persists immediately, matching addFiles()/removeImage()/moveImage()'s
   * already-immediate behavior for this gallery — only a not-yet-saved
   * product (no id yet) defers to save() the way those do too.
   *
   * Only PUTs the row being made primary — not the other rows this locally
   * demotes to primary: false. That's safe because
   * GiftCardProductImageServiceImpl.update() (backend) already demotes any
   * other primary row for the product server-side whenever primary=true is
   * sent, so the DB stays consistent with this optimistic local demotion
   * without a second round trip per other row.
   */
  async setPrimaryImage(index: number): Promise<void> {
    const row = this.images()[index];
    if (!row) {
      return;
    }

    const productId = this.productId();
    if (row.id != null && productId != null) {
      try {
        const response = await this.api.images.update(productId, {
          id: row.id,
          imageId: row.imageId,
          description: row.description,
          primary: true,
        });
        const saved = { ...row, ...this.toImageRow(response.data.payload) };
        this.images.set(this.images().map((r, i) => (i === index ? saved : { ...r, primary: false })));
      } catch (error) {
        this.errorMessage.set(this.extractErrorMessage(error));
      }
      return;
    }

    this.images.set(this.images().map((r, i) => ({ ...r, primary: i === index })));
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    /**
     * Snapshot into a plain array before resetting `input.value` — in a real
     * browser (unlike this suite's mocked events) `input.files` is a live
     * FileList tied to the input's current selection, and resetting `.value`
     * empties it in place. Reading it after the reset (or holding a
     * reference across it) silently loses every selected file.
     */
    const files = input.files ? Array.from(input.files) : null;
    input.value = '';
    void this.addFiles(files);
  }

  /**
   * Uploads the file bytes, then persists the gift_card_product_images row
   * immediately — matching removeImage()'s already-immediate behavior —
   * rather than waiting for the next "Update Gift Cards" click. Only
   * possible once a product exists to attach the image to; for a
   * brand-new, not-yet-saved product there's no productId yet, so the row
   * stays staged locally and save() bundles it into the product's own
   * create() call, same as before.
   */
  async addFiles(fileList: File[] | FileList | null): Promise<void> {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const hadNoImages = this.images().length === 0;
    this.errorMessage.set('');
    for (const file of Array.from(fileList)) {
      const uploadId = ++this.uploadSeq;
      this.uploadingFiles.set([...this.uploadingFiles(), { id: uploadId, name: file.name, progress: 0 }]);
      try {
        const response = await this.photos.upload({
          files: [file],
          onProgress: (percent) => this.setUploadProgress(uploadId, percent),
        });
        const imageId = this.extractUploadedImageId(response.data);
        if (imageId == null) {
          this.errorMessage.set(`Unable to process "${file.name}" — the uploaded file could not be registered. Please try again.`);
          continue;
        }
        const isFirstImageOverall = hadNoImages && this.images().length === 0;
        const previewUrl = URL.createObjectURL(file);
        const draft: ImageRow = { id: null, imageId, description: '', primary: isFirstImageOverall, previewUrl };

        const productId = this.productId();
        if (productId == null) {
          this.images.set([...this.images(), draft]);
          continue;
        }

        try {
          const created = await this.api.images.create(productId, {
            imageId: draft.imageId,
            description: draft.description,
            primary: draft.primary,
          });
          const saved = { ...draft, ...this.toImageRow(created.data.payload) };
          if (saved.previewUrl !== previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          this.images.set([...this.images(), saved]);
        } catch (error) {
          /**
           * The bytes already made it to S3 via photos.upload() above —
           * losing this row (and leaking its blob URL) would leave no trace
           * of it for the user to act on. Stage it locally instead, same
           * shape as the no-productId-yet branch above, so it's visible and
           * removable via removeImage() (which already revokes this exact
           * blob URL).
           */
          this.images.set([...this.images(), draft]);
          this.errorMessage.set(this.extractErrorMessage(error));
        }
      } catch (error) {
        this.errorMessage.set(this.extractErrorMessage(error));
      } finally {
        this.uploadingFiles.set(this.uploadingFiles().filter((entry) => entry.id !== uploadId));
      }
    }
  }

  private setUploadProgress(uploadId: number, percent: number): void {
    this.uploadingFiles.set(this.uploadingFiles().map((entry) => (entry.id === uploadId ? { ...entry, progress: percent } : entry)));
  }

  /**
   * Swaps two adjacent rows and persists the new order immediately once the
   * product exists (mirrors addFiles()/removeImage()'s already-immediate
   * behavior). Before the product's first save, images have no id yet — the
   * reorder is local-only and the eventual sequential create() calls in
   * save() naturally land in this array order.
   */
  async moveImage(index: number, direction: -1 | 1): Promise<void> {
    const current = this.images();
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= current.length) {
      return;
    }

    const reordered = [...current];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    this.images.set(reordered);

    const productId = this.productId();
    if (productId == null || reordered.some((row) => row.id == null)) {
      return;
    }

    try {
      await this.api.images.reorder(
        productId,
        reordered.map((row) => row.id as number | string),
      );
    } catch (error) {
      // Revert the optimistic reorder — otherwise the UI keeps showing an
      // order the server rejected, and the next successful moveImage()
      // would PUT an id list derived from that diverged state.
      this.images.set(current);
      this.errorMessage.set(this.extractErrorMessage(error));
    }
  }

  async removeImage(index: number): Promise<void> {
    const row = this.images()[index];
    if (!row) {
      return;
    }

    if (row.id != null) {
      const productId = this.productId();
      if (productId == null) {
        return;
      }
      try {
        await this.api.images.remove(productId, row.id);
      } catch (error) {
        this.errorMessage.set(this.extractErrorMessage(error));
        return;
      }
    }

    if (row.previewUrl) {
      URL.revokeObjectURL(row.previewUrl);
    }
    this.images.set(this.images().filter((_, i) => i !== index));
  }

  /**
   * Amounts are only ever included in the payload here when there's no
   * product yet (`isCreating`) — the default presets need to be created
   * together with the product before any of them has an id. Once a product
   * exists, editing an amount goes straight through `submitAmountEditor()`'s
   * own PUT the moment "Update" is clicked in its modal; this button no
   * longer touches amounts at all in that case.
   *
   * Preset amounts are still re-validated here regardless of `isCreating`
   * (defensive, same as before) for their own basic validity (name/amount
   * format) - but NOT re-checked against customAmountMin/Max here, unlike
   * the per-row Edit Amount modal (DEV-13995): the Preset Amounts table has
   * no per-row error display, so flagging an individual row here would be
   * invisible to the admin. Instead, a range that conflicts with an
   * existing preset is surfaced once, as a specific, actionable error on
   * the Min/Max fields themselves (validateRangeAgainstPresets) - e.g. "an
   * admin tightens min above an existing preset" now says exactly that,
   * rather than a generic "fix the highlighted preset amounts" pointing at
   * nothing visibly highlighted.
   */
  async save(): Promise<void> {
    const isCreating = this.productId() == null;

    /* The range's own shape (required/industry bounds/min<max) must be valid before it's
       checked against the existing presets below - otherwise an internally invalid range (e.g.
       min > max) could also get flagged as conflicting with a preset, masking the real "fix
       your range" error under a confusing second one. */
    if (this.customAmountRangeEnabled()) {
      const rangeErrors = validateCustomAmountRange({ min: this.customAmountMin(), max: this.customAmountMax() });
      if (!rangeErrors.min && !rangeErrors.max) {
        const range = this.currentCustomAmountRange();
        if (range) {
          Object.assign(rangeErrors, this.validateRangeAgainstPresets(range));
        }
      }
      this.customAmountRangeErrors.set(rangeErrors);
      if (rangeErrors.min || rangeErrors.max) {
        this.errorMessage.set('Fix the highlighted custom amount range before saving.');
        return;
      }
    } else {
      this.customAmountRangeErrors.set({});
    }

    const validatedAmounts = this.amounts().map((row) => ({ ...row, errors: validateAmountRow(row) }));
    this.amounts.set(validatedAmounts);
    if (validatedAmounts.some((row) => row.errors.name || row.errors.amount)) {
      this.errorMessage.set('Fix the highlighted preset amounts before saving.');
      return;
    }

    this.saving.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.syncContentHeader();
    try {
      let productId = this.productId();
      const productPayload = {
        active: this.active(),
        allowCustomAmount: this.allowCustomAmount(),
        customAmountMin: this.customAmountRangeEnabled() ? this.parseAmountSignal(this.customAmountMin()) : null,
        customAmountMax: this.customAmountRangeEnabled() ? this.parseAmountSignal(this.customAmountMax()) : null,
        ...(isCreating
          ? {
              amounts: this.amounts().map((row) => ({
                ...(row.id != null ? { id: row.id } : {}),
                name: row.name.trim(),
                description: row.description.trim(),
                amount: Number(row.amount),
              })),
            }
          : {}),
      };

      const response =
        productId == null ? await this.api.create(productPayload) : await this.api.update({ id: productId, ...productPayload });
      productId = response.data.payload?.id ?? productId;
      this.productId.set(productId);

      if (productId == null) {
        throw new Error('Gift card product save did not return an id.');
      }

      if (isCreating) {
        const amounts = response.data.payload?.amounts;
        if (amounts != null) {
          this.amounts.set(amounts.map((row) => this.toAmountRow(row)));
        }
      }

      // Only reachable for a brand-new product: addFiles()/setPrimaryImage()
      // persist immediately once a productId exists, so an already-existing
      // product's images never have a pending row left to sync here by the
      // time save() runs.
      if (isCreating) {
        const currentImages = this.images();
        for (let i = 0; i < currentImages.length; i++) {
          const row = currentImages[i];
          const payload = { imageId: row.imageId, description: row.description.trim(), primary: row.primary };
          const response = await this.api.images.create(productId, payload);
          const saved = { ...row, ...this.toImageRow(response.data.payload) };
          if (row.previewUrl && row.previewUrl.startsWith('blob:') && row.previewUrl !== saved.previewUrl) {
            URL.revokeObjectURL(row.previewUrl);
          }
          currentImages[i] = saved;
          this.images.set([...currentImages]);
        }
      }

      this.successMessage.set('Gift Cards updated.');
    } catch (error) {
      this.errorMessage.set(this.extractErrorMessage(error));
    } finally {
      this.saving.set(false);
      this.syncContentHeader();
    }
  }

  private toAmountRow(vo: { id?: number | string; name?: string; description?: string; amount?: number }): AmountRow {
    return {
      id: vo.id ?? null,
      name: vo.name ?? '',
      description: vo.description ?? '',
      amount: vo.amount != null ? String(vo.amount) : '',
      errors: {},
    };
  }

  /**
   * Omits `previewUrl` entirely (rather than setting it to `undefined`)
   * when `vo.preview` is absent, so merge sites like
   * `{ ...row, ...this.toImageRow(vo) }` (setPrimaryImage, addFiles,
   * save()) don't clobber an already-loaded thumbnail with `undefined`
   * just because a lightweight PUT response omitted `preview`.
   */
  private toImageRow(vo: {
    id?: number | string;
    imageId?: number | string;
    description?: string;
    primary?: boolean;
    preview?: string;
  }): ImageRow {
    return {
      id: vo.id ?? null,
      imageId: vo.imageId ?? '',
      description: vo.description ?? '',
      primary: vo.primary ?? false,
      ...(vo.preview !== undefined ? { previewUrl: vo.preview } : {}),
    };
  }

  /**
   * Extracts the image reference from PhotosApiService.upload()'s response.
   * The real backend (PhotoController#onAddPhotos) returns a JSON array of
   * ImagePathVo, whose identifying field is `uuid` (see ImagePath#getUuid) —
   * there is no `id` field. Handles a bare object too, defensively, in case
   * a single-file response shape is ever returned unwrapped. Returns null if
   * the response shape is unrecognized — caller should display an error.
   */
  private extractUploadedImageId(data: unknown): number | string | null {
    const payload = (data as { payload?: unknown } | null)?.payload;
    const entry = Array.isArray(payload) ? payload[0] : payload;
    if (entry && typeof entry === 'object') {
      return (entry as { uuid?: number | string }).uuid ?? null;
    }
    return null;
  }

  private extractErrorMessage(error: unknown): string {
    const body = (error as { data?: unknown } | null)?.data;
    return extractACMEErrorMessage(body) || GENERIC_ERROR;
  }
}
